import React, { useState, memo } from "react";
import { X } from "lucide-react";

const StandardFilter = memo(function StandardFilter({
  darkMode,
  isOpen,
  onClose,
  onApplyFilters,
  filterFields = [],
  filters = {},
  onFilterChange,
}) {
  const [localFilters, setLocalFilters] = useState(filters);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFilterChange && onFilterChange(key, value);
  };

  const handleApplyFilters = () => {
    onApplyFilters(localFilters);
    onClose();
  };

  const handleClearFilters = () => {
    const clearedFilters = {};
    filterFields.forEach((field) => {
      clearedFilters[field.key] = "";
    });
    setLocalFilters(clearedFilters);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className={`${
          darkMode ? "bg-[#2C2C2C] text-[#E0E0E0]" : "bg-white text-gray-900"
        } rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-auto`}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Filter Options</h3>
          <button
            onClick={onClose}
            className={`p-2 rounded-md transition-colors ${
              darkMode
                ? "text-[#A6A9C8] hover:text-[#E0E0E0] hover:bg-[#3C3C3C]"
                : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            }`}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          {filterFields.map((field) => (
            <div key={field.key}>
              <label
                className={`block text-sm font-medium mb-2 ${
                  darkMode ? "text-[#E0E0E0]" : "text-gray-700"
                }`}
              >
                {field.label}
              </label>

              {field.type === "select" ? (
                <select
                  value={localFilters[field.key] || ""}
                  onChange={(e) =>
                    handleFilterChange(field.key, e.target.value)
                  }
                  className={`w-full px-3 py-2 rounded-lg border ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 focus:border-transparent`}
                >
                  <option value="">All {field.label}</option>
                  {field.options?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : field.type === "date" ? (
                <DatePicker
                  value={localFilters[field.key] || ""}
                  onChange={(e) =>
                    handleFilterChange(field.key, e.target.value)
                  }
                  darkMode={darkMode}
                  label={field.label}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 focus:border-transparent`}
                />
              ) : field.type === "dateRange" ? (
                <div className="flex space-x-2">
                  <input
                    type="date"
                    placeholder="From Date"
                    value={localFilters[field.key]?.from || ""}
                    onChange={(e) =>
                      handleFilterChange(field.key, {
                        ...localFilters[field.key],
                        from: e.target.value,
                      })
                    }
                    className={`flex-1 px-3 py-2 rounded-lg border ${
                      darkMode
                        ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                        : "bg-white border-gray-300 text-gray-900"
                    } focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 focus:border-transparent`}
                  />
                  <input
                    type="date"
                    placeholder="To Date"
                    value={localFilters[field.key]?.to || ""}
                    onChange={(e) =>
                      handleFilterChange(field.key, {
                        ...localFilters[field.key],
                        to: e.target.value,
                      })
                    }
                    className={`flex-1 px-3 py-2 rounded-lg border ${
                      darkMode
                        ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                        : "bg-white border-gray-300 text-gray-900"
                    } focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 focus:border-transparent`}
                  />
                </div>
              ) : (
                <input
                  type="text"
                  placeholder={`Enter ${field.label}`}
                  value={localFilters[field.key] || ""}
                  onChange={(e) =>
                    handleFilterChange(field.key, e.target.value)
                  }
                  className={`w-full px-3 py-2 rounded-lg border ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 focus:border-transparent`}
                />
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={handleClearFilters}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              darkMode
                ? "bg-[#3C3C3C] text-[#E0E0E0] hover:bg-[#4C4C4C]"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            Clear All
          </button>
          <button
            onClick={handleApplyFilters}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              darkMode
                ? "bg-[#BB86FC] text-[#1C1C1C] hover:bg-[#A078D9]"
                : "rh-btn-primary hover:opacity-95"
            }`}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
});

export default StandardFilter;
